import { Component, EventEmitter, Input, OnInit, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { SharedService } from '../../../layouts/shared-service';
import { ContactsService } from '../../../services/contacts.service';
import { FboairportsService } from '../../../services/fboairports.service';
import { FbocontactsService } from '../../../services/fbocontacts.service';
// Services
import { FbosService } from '../../../services/fbos.service';
import { GroupsService } from '../../../services/groups.service';
import { AirportWatchService } from '../../../services/airportwatch.service';
import {
    CloseConfirmationComponent,
    CloseConfirmationData,
} from '../../../shared/components/close-confirmation/close-confirmation.component';
import { AppDialogWrapperService } from '../../../ni-components/app-dialog-wrapper/app-dialog-wrapper.service';
import { AccountType, UserRole } from 'src/app/enums/user-role';
import { localStorageAccessConstant } from 'src/app/constants/LocalStorageAccessConstant';
import { NgxUiLoaderService } from 'ngx-ui-loader';

@Component({
    selector: 'app-fbos-edit',
    styleUrls: ['./fbos-edit.component.scss'],
    templateUrl: './fbos-edit.component.html',
})
export class FbosEditComponent implements OnInit {
    @Input() fboInfo: any;
    @Input() fboAirportInfo: any;
    @Input() groupInfo: any;
    @Input() embed: boolean;
    @Output() saveClicked = new EventEmitter<any>();
    @Output() cancelClicked = new EventEmitter<any>();

    // Members
    breadcrumb: any[];
    pageTitle = 'Edit FBO';
    currentContact: any;
    contactsData: any;
    groups: Array<any>;
    availableAntennas: any[];
    availableAccountTypes: any[] = [{ type: AccountType[AccountType.Premium], value: AccountType.Premium }, { type: AccountType[AccountType.Freemium], value: AccountType.Freemium }];

    chartName = 'fbo-edit-chart';
    // Private Members

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fboService: FbosService,
        private fboAirportsService: FboairportsService,
        private fboContactsService: FbocontactsService,
        private contactsService: ContactsService,
        private groupsService: GroupsService,
        private sharedService: SharedService,
        private appDialogWrapperService: AppDialogWrapperService,
        private airportWatchService: AirportWatchService,
        private ngxLoader: NgxUiLoaderService
    ) {
        
    }

    get canChangeActive() {
        return (
            !this.embed &&
            this.sharedService.currentUser.role === UserRole.Conductor &&
            !this.sharedService.currentUser.impersonatedRole
        );
    }

    get isConductor() {
        return this.sharedService.currentUser.role === UserRole.Conductor;
    }

    get isGroupActive() {
        return this.groups?.find(x => x.oid === this.fboInfo.groupId)?.active ?? false;
    }

    async ngOnInit() {
        this.ngxLoader.startLoader(this.chartName);
        if (this.fboInfo) {
            await this.loadAdditionalFboInfo();
        } else {
            const id = this.route.snapshot.paramMap.get('id');
              this.fboInfo = await this.fboService
                .get({
                    oid: id,
                }).toPromise();

            this.fboAirportInfo = await this.fboAirportsService
                .getForFbo({
                    oid: id,
                }).toPromise();
            await this.loadAdditionalFboInfo();
        }
        this.ngxLoader.stopLoader(this.chartName);
    }
    saveEdit(): void {
        if (sessionStorage.getItem(localStorageAccessConstant.isNetworkFbo)) {
            sessionStorage.removeItem(localStorageAccessConstant.isNewFbo);
        }
        this.fboAirportInfo.fboId = this.fboInfo.oid;
        this.fboService.update(this.fboInfo).subscribe(() => {
            this.fboAirportsService
                .update(this.fboAirportInfo)
                .subscribe(() => {
                    this.saveClicked.emit(this.fboInfo);
                    this.navigateToParent();
                });
        });
    }

    cancelEdit(): void {
        if (sessionStorage.getItem(localStorageAccessConstant.isNewFbo)) {
            this.fboService.remove(this.fboInfo).subscribe(() => {
                sessionStorage.removeItem(localStorageAccessConstant.isNewFbo);
                this.saveClicked.emit(this.fboInfo);
            });
        } else {
            this.cancelClicked.emit();
            this.navigateToParent();
        }
    }

    navigateToParent(): void {
        if (
            this.sharedService.currentUser.role === UserRole.Conductor &&
            !this.sharedService.currentUser.impersonatedRole
        ) {
            if (this.groupInfo) {
                this.router.navigate([
                    '/default-layout/groups/' + this.groupInfo.oid,
                ]);
            } else {
                this.router.navigate(['/default-layout/groups/']);
            }
        } else {
            this.router.navigate(['/default-layout/fbos/']);
        }
    }

    contactDeleted(record: any): void {
        this.fboContactsService.remove(record).subscribe(() => {
            this.fboContactsService
                .getForFbo(this.fboInfo)
                .subscribe((data: any) => (this.contactsData = data));
        });
    }

    newContactClicked(): void {
        this.currentContact = {
            oid: 0,
        };
    }

    editContactClicked(record: any): void {
        this.contactsService
            .get({
                oid: record.contactId,
            })
            .subscribe((data: any) => (this.currentContact = data));
    }

    saveEditContactClicked(): void {
        this.contactsService.update(this.currentContact).subscribe(() => {
            this.currentContact = null;
        });
    }

    cancelEditContactClicked(): void {
        this.currentContact = null;
    }

    onActiveToggle(event): void {
        if (event.checked && this.fboInfo.expirationDate) {
            const dialogWrapper = this.appDialogWrapperService.open<boolean>({
                title: 'Account Expired!',
                component: CloseConfirmationComponent,
                data: {
                    cancel: 'Cancel',
                    customText:
                        'Account Expiry date is set. If you activate this account, it will be removed.',
                    customTitle: 'Account Expired!',
                    ok: 'Set Active',
                } as CloseConfirmationData,
                width: '450px'
            });
            dialogWrapper.afterClosed().subscribe((confirmed: boolean) => {
                if (!confirmed) {
                    this.fboInfo.active = false;
                    return;
                }

                this.fboInfo.expirationDate = null;
            });
        }
    }

    onGroupChange(event: any): void {
        if(this.isGroupActive){
            this.fboInfo.active = true;
            return;
        }
        this.fboInfo.active = false;
    }

    // Private Methods
    private async loadAdditionalFboInfo(): Promise<void> {
        [this.contactsData, this.groups, this.availableAntennas] = await Promise.all([
            this.fboContactsService.getForFbo(this.fboInfo).toPromise(),
            this.groupsService.getAllGroups().toPromise(),
            this.airportWatchService.getUnassignedAntennaBoxes(this.fboInfo.antennaName).toPromise()
        ]);
    }
}
